import {
  ApolloError,
  ServerError,
  useLazyQuery as apolloUseLazyQuery,
  useMutation as apolloUseMutation,
  useQuery as apolloUseQuery,
} from "@apollo/client";
import { useCallback } from "react";
import { useToastMessage } from "src/contexts/Toast.context";
import { Button } from "~components";

// Re-export types the generated hooks reference on the ApolloReactHooks namespace
export type { LazyQueryHookOptions, MutationHookOptions, QueryHookOptions } from "@apollo/client";

type GraphQLError = { message: string; locations: unknown; path: unknown };

/** Wraps Apollo's `useQuery` with default `onError` behavior. */
export const useQuery: typeof apolloUseQuery = function (query, options) {
  const { showToast } = useToastMessage();
  const onError = useCallback(
    (error: ApolloError) => wrapOnError("query", options?.onError, showToast)(error),
    [options?.onError, showToast],
  );
  return apolloUseQuery(query, { ...options, onError });
};

/** Wraps Apollo's `useLazyQuery` with default `onError` behavior. */
export const useLazyQuery: typeof apolloUseLazyQuery = function (query, options) {
  const { showToast } = useToastMessage();
  const onError = useCallback(
    (error: ApolloError) => wrapOnError("query", options?.onError, showToast)(error),
    [options?.onError, showToast],
  );
  // Instrument execute with our onError
  const [rawExecute, result] = apolloUseLazyQuery(query, { ...options, onError });
  const execute = useCallback(
    (opts?: any) => {
      return rawExecute({ ...opts, onError: wrapOnError("query", opts?.onError, showToast) });
    },
    [rawExecute, showToast],
  );
  return [execute, result];
};

/**
 * Wraps Apollo's `useMutation` with default `onError` behavior.
 *
 * "Graphql validation error" (stale app) is always intercepted globally before any call-time
 * onError the component provides — a schema mismatch is not something the component can recover
 * from. For all other errors, the call-time onError (if any) takes precedence over the default
 * toast handler, matching Apollo's native behavior.
 */
export const useMutation: typeof apolloUseMutation = function (mutation, options) {
  const { showToast } = useToastMessage();
  const onError = useCallback(
    (error: ApolloError) => wrapOnError("mutation", options?.onError, showToast)(error),
    [options?.onError, showToast],
  );
  // Instrument execute with our onError
  const [rawExecute, result] = apolloUseMutation(mutation, { ...options, onError });
  const execute = useCallback(
    (opts?: any) => {
      return rawExecute({ ...opts, onError: wrapOnError("mutation", opts?.onError, showToast) });
    },
    [rawExecute, showToast],
  );
  return [execute, result] as any;
};

/** Wraps an `onError` to intercept "Graphql validation error" (stale app) before it reaches the handler. */
function wrapOnError(
  type: "query" | "mutation",
  onError: ((error: ApolloError) => void) | undefined,
  showToast: ReturnType<typeof useToastMessage>["showToast"],
): (error: ApolloError) => void {
  return (error) => {
    const message = extractErrorMessage(error);
    if (message === "Graphql validation error") {
      const { title, body } = generateToastContent(message);
      showToast({ variant: "error", title, body });
      console.error(message);
      return;
    }
    if (onError) {
      onError(error);
    } else {
      toastOnError(type, showToast, error);
    }
  };
}

function extractErrorMessage(error: ApolloError): string {
  const result = (error.networkError as ServerError)?.result as { errors?: GraphQLError[] } | undefined;
  const errors = result?.errors;
  return !result || !errors || errors.length === 0 ? error.message : errors.map((e) => e.message).join(", ");
}

function toastOnError(
  type: "query" | "mutation",
  showToast: ReturnType<typeof useToastMessage>["showToast"],
  error: ApolloError,
): void {
  let message = extractErrorMessage(error);

  if (message.startsWith("Fetch failed calling") && type === "mutation") {
    message =
      "The operation timed out, but may actually succeed, so please reload the page or wait before trying again.";
  }

  const { title, body } = generateToastContent(message);
  showToast({ variant: "error", title, body });

  console.error(message);

  // Re-throw for mutations so that `await saveMutation(...)` callers still get a rejection,
  // matching Apollo's default behavior. This only fires when no custom onError was provided —
  // components with their own onError handler bypass this entirely (Apollo picks call-level first).
  if (type === "mutation") {
    throw error;
  }
}

function generateToastContent(message: string): { title: string; body: React.ReactNode | undefined } {
  if (message === "Graphql validation error") {
    return {
      title: "This version of Checkout is out of date and your request could not be processed.",
      body: (
        <Button size="small" onClick={() => window.location.reload()}>
          Reload
        </Button>
      ),
    };
  }
  return { title: message, body: undefined };
}
